import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTrashAlt } from '@fortawesome/pro-solid-svg-icons';

import TeamModal from './team_modal';
import TeamMemberModal from './team_member_modal';
import { get, post, put, deleteRequest } from 'helpers/api';
import TeamMembersList from './team_members_list';
import { debounce } from 'lodash';

export default function Team(props) {
  const blankTeamMember = {
    cellPhoneNumber: '',
    email: '',
    name: '',
    jobTitle: '',
    officeName: '',
    licenseNumber: '',
    phoneNumber: ''
  };
  const teamMembersUrl = `${props.teamsUrl}/${props.id}/team_members`;

  const [editTeamModalOpen, setEditTeamModalOpen] = React.useState(false);
  const [newTeamName, setNewTeamName] = React.useState(props.name);
  const [teamMembers, setTeamMembers] = React.useState([]);
  const [sortingTeamMembers, setSortingTeamMembers] = React.useState(null);
  const [teamMemberModalOpen, setTeamMemberModalOpen] = React.useState(false);
  const [newTeamMember, setNewTeamMember] = React.useState(blankTeamMember);

  function closeTeamModal() {
    setEditTeamModalOpen(false);
    setNewTeamName(props.name);
  }

  function closeTeamMemberModal() {
    setTeamMemberModalOpen(false);
    setNewTeamMember(blankTeamMember);
  }

  useEffect(() => {
    getTeamMembers();
  }, []);

  const pushSortOrders = debounce(() => {
    if (sortingTeamMembers && sortingTeamMembers.length > 0) {
      setTeamMembers(sortingTeamMembers);
      const teamMembersAttributes = sortingTeamMembers.map((member, idx) => ({
        id: member.id, sortOrder: idx
      }));

      put(`${props.teamsUrl}/${props.id}`, { emailTeam: { teamMembersAttributes } });

      props.updatePreview({ emailTeamMembers: sortingTeamMembers });
      setSortingTeamMembers(null);
    }
  }, 500);

  const handleReorderTeamMembers = useCallback((dragIndex, dropIndex) => {
    const members = Object.assign([], teamMembers);
    const member = members[dragIndex];
    members.splice(dragIndex, 1);
    members.splice(dropIndex, 0, member);

    setSortingTeamMembers(members);
  }, [teamMembers]);

  const beginEditTeam = () => {
    setEditTeamModalOpen(false);
    props.editTeam(props.id, newTeamName);
  };

  const getTeamMembers = async () => {
    const response = await get(teamMembersUrl);
    if (Array.isArray(response)) {
      const members = response.map((member) => {
        return member.teamMember;
      });
      setTeamMembers(members);
    } else {
      setTeamMembers([]);
    }
  };

  const createTeamMember = async () => {
    setTeamMemberModalOpen(false);
    await post(teamMembersUrl, { ...newTeamMember, sortOrder: teamMembers.length }).then((response) => {
      setTeamMembers([...teamMembers, response.teamMember]);
      setNewTeamMember(blankTeamMember);
      props.updatePreview({ emailTeamMembers: [...teamMembers, response.teamMember] });
    });
  };

  const editTeamMember = async (id, teamMember) => {
    await put(`${teamMembersUrl}/${id}`, teamMember).then((response) => {
      const updatedTeamMembers = teamMembers.map((teamMember) => {
        if (teamMember.id === id) {
          return response.teamMember;
        }
        return teamMember;
      });
      setTeamMembers(updatedTeamMembers);
      props.updatePreview({ emailTeamMembers: updatedTeamMembers });
    });
  };

  const deleteTeamMember = async (id) => {
    await deleteRequest(`${teamMembersUrl}/${id}`).then(() => {
      const updatedTeamMembers = teamMembers.filter(teamMember => teamMember.id !== id);
      setTeamMembers(updatedTeamMembers);
      props.updatePreview({ emailTeamMembers: updatedTeamMembers });
    });
  };

  return (
    <React.Fragment>
      <TeamModal
        editOrCreateTeam={beginEditTeam}
        modalOpen={editTeamModalOpen}
        newTeam={false}
        setTeamName={setNewTeamName}
        teamName={newTeamName}
        onModalClose={closeTeamModal}
      />
      <TeamMemberModal
        editOrCreateTeamMember={createTeamMember}
        modalOpen={teamMemberModalOpen}
        newTeamMember={true}
        setTeamMember={setNewTeamMember}
        teamMember={newTeamMember}
        onModalClose={closeTeamMemberModal}
      />
      <div className="border p-3">
        <div className="d-flex justify-content-between mb-3">
          {props.name}
          <div className="d-flex justify-content-end">
            <FontAwesomeIcon
              className="clickable"
              icon={faPencilAlt}
              size="sm"
              onClick={() => setEditTeamModalOpen(true)}
            />
            <FontAwesomeIcon
              className="clickable ml-3 trash-icon"
              icon={faTrashAlt}
              onClick={() => props.deleteTeam(props.id)}
            />
          </div>
        </div>
        <div className="team">
          <TeamMembersList
            teamId={props.id}
            teamMembers={sortingTeamMembers || teamMembers}
            onDeleteTeamMember={deleteTeamMember}
            onEditTeamMember={editTeamMember}
            onReorderSave={pushSortOrders}
            onReorderTeamMembers={handleReorderTeamMembers}
          />
          <button className="btn" type="button" onClick={() => setTeamMemberModalOpen(true)}>
            + Add Footer Contact
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}

Team.propTypes = {
  deleteTeam: PropTypes.func,
  editTeam: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  teamsUrl: PropTypes.string.isRequired,
  updatePreview: PropTypes.func.isRequired
};
